import { FieldTrigger } from '@/components/ui/field-trigger'
import { ToggleButton } from '@/components/ui/toggle-button'
import { Calendar, CalendarDayButton } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import dayjs from '@/lib/dayjs'
import {
  clampReportDate,
  clampReportDateRange,
  type ReportDateModeValue,
  type ReportDateRange,
} from '@/lib/report-date-range'
import { cn, toCalendarDate, toYmdFromDate } from '@/lib/utils'
import type { ConfigType, Dayjs } from 'dayjs'
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react'
import { createContext, useContext, useState } from 'react'
import type { DayButtonProps, Modifiers, PropsRange, PropsSingle } from 'react-day-picker'

/** A preset button: `getRange` receives the bar's `now` and returns the range to apply. */
export type ReportDateRangePreset = {
  key: string
  label: string
  getRange: (now: Dayjs) => ReportDateRange
}

/** What `onApply` receives: the chosen range, plus `date_mode` when `appliedDateMode` is set. */
export type ReportDateRangeSelection = ReportDateRange & {
  date_mode?: ReportDateModeValue
}

/** The applied range and the selectable bounds, each `YYYY-MM-DD`. */
export type ReportDateRangeInitialFilters = {
  start_date?: string | null
  end_date?: string | null
  minDate?: string | null
  maxDate?: string | null
}

type ReportDateRangeCalendarIntent = 'historical' | 'future'

export type ReportDateRangeFilterBarProps = {
  additionalPresets?: ReportDateRangePreset[]
  appliedDateMode?: ReportDateModeValue
  calendarIntent?: ReportDateRangeCalendarIntent
  className?: string
  initialFilters?: ReportDateRangeInitialFilters
  maximumRangeDays?: number
  numberOfMonths?: number
  onApply: (range: ReportDateRangeSelection) => void
  now?: ConfigType
  /** Limits the presets shown to these keys; an empty array hides them all. */
  presetKeys?: string[]
  /** Replaces the default and additional presets. */
  presets?: ReportDateRangePreset[]
  disabled?: boolean
  showSingleDayAsRange?: boolean
  showTriggerChevron?: boolean
  singleDate?: boolean
  triggerClassName?: string
}

type ReportRangeDaySelectHandler = (date: Date, modifiers: Modifiers) => void

const DEFAULT_PRESET_DEFINITIONS: ReportDateRangePreset[] = [
  {
    key: 'today',
    label: 'Today',
    getRange: (now) => ({
      start_date: now.format('YYYY-MM-DD'),
      end_date: now.format('YYYY-MM-DD'),
    }),
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    getRange: (now) => {
      const yesterday = now.subtract(1, 'day')

      return {
        start_date: yesterday.format('YYYY-MM-DD'),
        end_date: yesterday.format('YYYY-MM-DD'),
      }
    },
  },
  {
    key: 'this_week',
    label: 'This week',
    getRange: (now) => ({
      start_date: now.startOf('week').format('YYYY-MM-DD'),
      end_date: now.endOf('week').format('YYYY-MM-DD'),
    }),
  },
  {
    key: 'last_week',
    label: 'Last week',
    getRange: (now) => {
      const lastWeek = now.subtract(1, 'week')

      return {
        start_date: lastWeek.startOf('week').format('YYYY-MM-DD'),
        end_date: lastWeek.endOf('week').format('YYYY-MM-DD'),
      }
    },
  },
  {
    key: 'this_month',
    label: 'This month',
    getRange: (now) => ({
      start_date: now.startOf('month').format('YYYY-MM-DD'),
      end_date: now.endOf('month').format('YYYY-MM-DD'),
    }),
  },
  {
    key: 'last_month',
    label: 'Last month',
    getRange: (now) => {
      const lastMonth = now.subtract(1, 'month')

      return {
        start_date: lastMonth.startOf('month').format('YYYY-MM-DD'),
        end_date: lastMonth.endOf('month').format('YYYY-MM-DD'),
      }
    },
  },
  {
    key: 'this_year',
    label: 'This year',
    getRange: (now) => {
      const currentYear = now.year()

      return {
        start_date: now.year(currentYear).month(0).startOf('month').format('YYYY-MM-DD'),
        end_date: now.year(currentYear).month(11).endOf('month').format('YYYY-MM-DD'),
      }
    },
  },
  {
    key: 'last_year',
    label: 'Last year',
    getRange: (now) => {
      const lastYear = now.year() - 1

      return {
        start_date: now.year(lastYear).month(0).startOf('month').format('YYYY-MM-DD'),
        end_date: now.year(lastYear).month(11).endOf('month').format('YYYY-MM-DD'),
      }
    },
  },
]

const ignoreCalendarRangeSelect = () => {}

const pendingEndPreviewCalendarClassNames = {
  day_button:
    'data-[range-end=true]:bg-transparent data-[range-end=true]:text-accentSecondary-foreground data-[range-end=true]:hover:bg-transparent data-[range-end=true]:hover:text-accentSecondary-foreground',
}

const ReportRangeDayButtonContext =
  createContext<ReportRangeDaySelectHandler>(ignoreCalendarRangeSelect)

const reportRangeCalendarComponents = {
  DayButton: ReportRangeDayButton,
}

function ReportRangeDayButton({ day, modifiers, ...props }: DayButtonProps) {
  const onDayDoubleSelect = useContext(ReportRangeDayButtonContext)

  return (
    <CalendarDayButton
      {...props}
      day={day}
      modifiers={modifiers}
      onDoubleClick={(event) => {
        props.onDoubleClick?.(event)
        onDayDoubleSelect?.(day.date, modifiers)
      }}
    />
  )
}

function compareDates(left: Date, right: Date) {
  const leftYmd = toYmdFromDate(left)
  const rightYmd = toYmdFromDate(right)

  if (leftYmd === rightYmd) {
    return 0
  }

  return leftYmd < rightYmd ? -1 : 1
}

function formatRangeLabel(
  { start_date, end_date }: Pick<ReportDateRangeInitialFilters, 'start_date' | 'end_date'>,
  { showSingleDayAsRange = false }: { showSingleDayAsRange?: boolean } = {}
) {
  if (!start_date || !end_date) {
    return 'Choose dates'
  }

  const startDay = dayjs(start_date)
  const endDay = dayjs(end_date)

  if (startDay.isSame(endDay, 'day') && !showSingleDayAsRange) {
    return startDay.format('MMM D, YYYY')
  }

  return `${startDay.format('MMM D, YYYY')} - ${endDay.format('MMM D, YYYY')}`
}

function buildDisabledMatcher(
  minDate: string | null | undefined,
  maxDate: string | null | undefined,
  maximumEndDate: string | undefined
) {
  if (!minDate && !maxDate && !maximumEndDate) {
    return undefined
  }

  return (date: Date) => {
    const dateYmd = toYmdFromDate(date)

    if (minDate && dateYmd < minDate) {
      return true
    }

    if (maxDate && dateYmd > maxDate) {
      return true
    }

    if (maximumEndDate && dateYmd > maximumEndDate) {
      return true
    }

    return false
  }
}

function withAppliedDateMode(
  range: ReportDateRange,
  appliedDateMode: ReportDateModeValue | undefined
): ReportDateRangeSelection {
  return appliedDateMode ? { ...range, date_mode: appliedDateMode } : range
}

function getDefaultCalendarMonth({
  calendarIntent,
  maxDate,
  minDate,
  now,
  numberOfMonths,
  singleDate,
}: {
  calendarIntent: ReportDateRangeCalendarIntent
  maxDate: string | null | undefined
  minDate: string | null | undefined
  now: Dayjs
  numberOfMonths: number
  singleDate: boolean
}) {
  const anchorDate = clampReportDate(now.format('YYYY-MM-DD'), minDate, maxDate)
  const monthOffset =
    calendarIntent === 'future' || singleDate ? 0 : Math.max(numberOfMonths - 1, 0)

  return toCalendarDate(dayjs(anchorDate).subtract(monthOffset, 'month').startOf('month'))
}

export default function ReportDateRangeFilterBar({
  additionalPresets = [],
  appliedDateMode,
  calendarIntent = 'historical',
  className,
  initialFilters = {},
  maximumRangeDays,
  numberOfMonths = 2,
  onApply,
  now = dayjs(),
  presetKeys,
  presets,
  disabled = false,
  showSingleDayAsRange = false,
  showTriggerChevron = false,
  singleDate = false,
  triggerClassName,
}: ReportDateRangeFilterBarProps) {
  const resolvedNow = dayjs(now)
  const [open, setOpen] = useState(false)
  const [draftRange, setDraftRange] = useState(() => ({
    from: initialFilters.start_date ? toCalendarDate(initialFilters.start_date) : undefined,
    to: initialFilters.end_date ? toCalendarDate(initialFilters.end_date) : undefined,
  }))
  const [isSelectingEnd, setIsSelectingEnd] = useState(false)
  const [hoverDate, setHoverDate] = useState<Date | undefined>(undefined)

  const selectedRange = {
    start_date: initialFilters.start_date,
    end_date: initialFilters.end_date,
  }
  const selectedPresetKeys = Array.isArray(presetKeys) ? new Set(presetKeys) : null
  const visiblePresets = (presets ?? [...DEFAULT_PRESET_DEFINITIONS, ...additionalPresets]).filter(
    ({ key }) => !selectedPresetKeys || selectedPresetKeys.has(key)
  )
  const maximumEndDate =
    isSelectingEnd && draftRange.from && maximumRangeDays
      ? dayjs(toYmdFromDate(draftRange.from))
          .add(maximumRangeDays - 1, 'day')
          .format('YYYY-MM-DD')
      : undefined
  const disabledMatcher = buildDisabledMatcher(
    initialFilters.minDate,
    initialFilters.maxDate,
    maximumEndDate
  )
  const defaultCalendarMonth = getDefaultCalendarMonth({
    calendarIntent,
    maxDate: initialFilters.maxDate,
    minDate: initialFilters.minDate,
    now: resolvedNow,
    numberOfMonths,
    singleDate,
  })
  const visualRange =
    isSelectingEnd && draftRange.from
      ? {
          from: draftRange.from,
          to:
            hoverDate && compareDates(hoverDate, draftRange.from) >= 0 ? hoverDate : draftRange.to,
        }
      : draftRange

  const handleOpenChange = (nextOpen: boolean) => {
    if (disabled) {
      setOpen(false)
      return
    }

    setOpen(nextOpen)
    setIsSelectingEnd(false)
    setHoverDate(undefined)

    if (nextOpen) {
      setDraftRange({
        from: initialFilters.start_date ? toCalendarDate(initialFilters.start_date) : undefined,
        to: initialFilters.end_date ? toCalendarDate(initialFilters.end_date) : undefined,
      })
    }
  }

  const handleCalendarDayMouseEnter = (date: Date, modifiers: Modifiers = {}) => {
    if (!isSelectingEnd || modifiers.disabled) {
      return
    }

    setHoverDate(date)
  }

  const handleCalendarDayClick = (date: Date, modifiers: Modifiers = {}) => {
    if (modifiers.disabled) {
      return
    }

    if (singleDate) {
      const dateYmd = clampReportDate(
        toYmdFromDate(date),
        initialFilters.minDate,
        initialFilters.maxDate
      )

      setDraftRange({ from: date, to: date })
      onApply(
        withAppliedDateMode(
          {
            start_date: dateYmd,
            end_date: dateYmd,
          },
          appliedDateMode
        )
      )
      setOpen(false)
      return
    }

    if (!isSelectingEnd || !draftRange.from) {
      setDraftRange({ from: date, to: undefined })
      setIsSelectingEnd(true)
      setHoverDate(undefined)
      return
    }

    if (compareDates(date, draftRange.from) < 0) {
      setDraftRange({ from: date, to: undefined })
      setHoverDate(undefined)
      return
    }

    const nextRange = {
      from: draftRange.from,
      to: date,
    }
    setDraftRange(nextRange)
    setIsSelectingEnd(false)
    setHoverDate(undefined)
    onApply(
      withAppliedDateMode(
        clampReportDateRange(
          {
            start_date: toYmdFromDate(nextRange.from),
            end_date: toYmdFromDate(nextRange.to),
          },
          initialFilters.minDate,
          initialFilters.maxDate
        ),
        appliedDateMode
      )
    )
    setOpen(false)
  }

  const handlePresetSelect = (presetRange: ReportDateRange) => {
    onApply(
      withAppliedDateMode(
        clampReportDateRange(presetRange, initialFilters.minDate, initialFilters.maxDate),
        appliedDateMode
      )
    )
    setOpen(false)
  }

  const handleSingleDayDoubleClick = (date: Date, modifiers: Modifiers = {}) => {
    if (modifiers.disabled) {
      return
    }

    const dateYmd = toYmdFromDate(date)

    setDraftRange({ from: date, to: date })
    setIsSelectingEnd(false)
    setHoverDate(undefined)
    onApply(
      withAppliedDateMode(
        clampReportDateRange(
          {
            start_date: dateYmd,
            end_date: dateYmd,
          },
          initialFilters.minDate,
          initialFilters.maxDate
        ),
        appliedDateMode
      )
    )
    setOpen(false)
  }

  // DayPicker types `selected` by `mode`, so the pair is chosen together.
  const calendarSelection: PropsSingle | PropsRange = singleDate
    ? { mode: 'single', selected: draftRange.to }
    : { mode: 'range', selected: visualRange }

  const formattedLabel = formatRangeLabel(
    singleDate
      ? { start_date: selectedRange.end_date, end_date: selectedRange.end_date }
      : selectedRange,
    { showSingleDayAsRange }
  )

  return (
    <div className={cn('flex justify-end', className)}>
      <Popover open={disabled ? false : open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <FieldTrigger
            disabled={disabled}
            size="sm"
            className={cn('w-[250px] justify-start gap-2 text-left', triggerClassName)}
          >
            <CalendarIcon className="size-4 shrink-0" />
            <span className={cn('truncate', showTriggerChevron && 'min-w-0 flex-1 text-left')}>
              {singleDate ? `As of ${formattedLabel}` : formattedLabel}
            </span>
            {showTriggerChevron ? (
              <ChevronDown
                aria-hidden="true"
                className="ml-auto size-4 shrink-0 text-muted-foreground"
              />
            ) : null}
          </FieldTrigger>
        </PopoverTrigger>
        <PopoverContent size="auto" padding="none" align="end">
          <div>
            <ReportRangeDayButtonContext.Provider value={handleSingleDayDoubleClick}>
              <Calendar
                {...calendarSelection}
                numberOfMonths={singleDate ? 1 : numberOfMonths}
                defaultMonth={defaultCalendarMonth}
                buttonVariant="outline"
                className="w-full p-3 pb-2"
                classNames={
                  !singleDate && isSelectingEnd ? pendingEndPreviewCalendarClassNames : undefined
                }
                showOutsideDays={false}
                onSelect={ignoreCalendarRangeSelect}
                onDayClick={handleCalendarDayClick}
                onDayMouseEnter={handleCalendarDayMouseEnter}
                disabled={disabledMatcher}
                components={singleDate ? undefined : reportRangeCalendarComponents}
              />
            </ReportRangeDayButtonContext.Provider>
            {!singleDate ? (
              <p className="px-4 pb-2 text-sm text-muted-foreground">
                * Double-click a day to select it
              </p>
            ) : null}
            {visiblePresets.length > 0 ? (
              <div
                data-testid="report-date-range-presets"
                className="grid w-full grid-cols-2 gap-x-2 gap-y-1 border-t p-3"
              >
                {visiblePresets.map((preset) => {
                  const presetRange = clampReportDateRange(
                    preset.getRange(resolvedNow),
                    initialFilters.minDate,
                    initialFilters.maxDate
                  )
                  const isActive =
                    presetRange.start_date === selectedRange.start_date &&
                    presetRange.end_date === selectedRange.end_date

                  return (
                    <ToggleButton
                      key={preset.key}
                      type="button"
                      size="sm"
                      pressed={isActive}
                      className="min-w-0 justify-center whitespace-nowrap px-2"
                      onClick={() => handlePresetSelect(presetRange)}
                    >
                      {preset.label}
                    </ToggleButton>
                  )
                })}
              </div>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
